import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2, X } from 'lucide-react';
import { Input } from './ui/input';
import { placesApi } from '../lib/api';

/**
 * Composant d'autocomplétion d'adresse avec Google Places API
 * @param {string} value - Valeur actuelle de l'input
 * @param {function} onChange - Callback quand l'adresse change (reçoit l'objet adresse complet)
 * @param {string} placeholder - Placeholder de l'input
 * @param {string} label - Label optionnel
 * @param {string} className - Classes CSS additionnelles
 */
const AddressAutocomplete = ({ 
  value = '', 
  onChange, 
  onAddressSelect,
  placeholder = "Entrez une adresse...",
  label,
  className = '',
  error,
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync external value changes
  useEffect(() => {
    if (value && value !== inputValue) {
      setInputValue(value);
    }
  }, [value]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions with debounce
  const fetchSuggestions = async (input) => {
    if (!input || input.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await placesApi.autocomplete(input);
      setSuggestions(response.data.predictions || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Autocomplete error:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSelectedAddress(null);

    // Debounce API calls
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);

    // Notify parent of text change
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleSuggestionClick = async (suggestion) => {
    setInputValue(suggestion.description);
    setShowSuggestions(false);
    setSuggestions([]);
    setIsLoading(true);

    try {
      // Get full address details
      const response = await placesApi.getDetails(suggestion.place_id);
      const addressData = response.data;
      
      setSelectedAddress(addressData);
      
      // Notify parent with full address object
      if (onAddressSelect) {
        onAddressSelect({
          street: addressData.street || '',
          city: addressData.city || '',
          postal_code: addressData.postal_code || '',
          country: addressData.country || '',
          formatted_address: addressData.formatted_address || suggestion.description,
          lat: addressData.lat,
          lng: addressData.lng
        });
      }
    } catch (error) {
      console.error('Place details error:', error);
      // Fallback: use description as city
      if (onAddressSelect) {
        onAddressSelect({
          street: '',
          city: suggestion.main_text || suggestion.description.split(',')[0],
          postal_code: '',
          country: '',
          formatted_address: suggestion.description
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setInputValue('');
    setSelectedAddress(null);
    setSuggestions([]);
    if (onChange) onChange('');
    if (onAddressSelect) onAddressSelect(null);
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          {label}
        </label>
      )}
      
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <MapPin className="w-4 h-4" />
          )}
        </div>
        
        <Input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => inputValue.length >= 3 && setShowSuggestions(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={`pl-10 pr-10 ${error ? 'border-red-500' : ''} ${selectedAddress ? 'bg-green-50 border-green-300' : ''}`}
          data-testid="address-autocomplete-input"
        />
        
        {inputValue && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Selected address indicator */}
      {selectedAddress && selectedAddress.city && (
        <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          {selectedAddress.city}{selectedAddress.postal_code ? `, ${selectedAddress.postal_code}` : ''}
        </p>
      )}

      {/* Error message */}
      {error && (
        <p className="text-xs text-red-500 mt-1">{error}</p>
      )}

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.place_id || index}
              type="button"
              onClick={() => handleSuggestionClick(suggestion)}
              className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-start gap-3 border-b border-slate-100 last:border-b-0"
              data-testid={`suggestion-${index}`}
            >
              <MapPin className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-slate-900 text-sm">
                  {suggestion.main_text || suggestion.description.split(',')[0]}
                </p>
                <p className="text-xs text-slate-500">
                  {suggestion.secondary_text || suggestion.description}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results message */}
      {showSuggestions && inputValue.length >= 3 && suggestions.length === 0 && !isLoading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-4 text-center">
          <p className="text-sm text-slate-500">Aucune adresse trouvée</p>
          <p className="text-xs text-slate-400 mt-1">Essayez d&apos;être plus précis</p>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
